// crewbit-admin/src/hooks/useCreateScheduleEntry.ts
//
// Schedule Module Phase B.1 — paired hook for `create_schedule_entry` RPC
// (Migration 312 + A13 corrections in 313).
// Spec: docs/references/schedule/SCHEDULE_MODULE_WIRING_v1.md §4.1.
//
// Mirror of crewbit-mobile/hooks/useCreateScheduleEntry.ts; bodies byte-identical
// modulo import paths (per feedback_admin_pair_must_match_mobile_body.md).

import { useCallback } from 'react';
import { callScheduleRpc, type ScheduleRpcResult } from '../lib/scheduleRpc';
import type { ConflictDetail } from '../lib/errorMap';

export type ScheduleEntryType =
  | 'job'
  | 'booking'
  | 'event'
  | 'personal'
  | 'block'
  | 'class'
  | 'subscribed';

export interface CreateScheduleEntryInput {
  type: ScheduleEntryType;
  title: string;
  /** ISO-8601 timestamptz. */
  startAt: string;
  /** ISO-8601 timestamptz. */
  endAt: string;
  /** public.users.id[]. Operator's own publicUserId required if caller is operator. */
  assignedTo?: string[];
  resourceId?: string;
  jobId?: string;
  bookingId?: string;
  projectId?: string;
  clientId?: string;
  locationName?: string;
  locationAddress?: string;
  locationLat?: number;
  locationLng?: number;
  /** RFC 5545 RRULE. */
  recurrenceRule?: string;
  /** YYYY-MM-DD. */
  recurrenceEnd?: string;
  color?: string;
  capacity?: number;
  notes?: string;
  /** REQUIRED — UUID v4 from form-load (WIRING spec §C "Idempotency key generation"). */
  idempotencyKey: string;
  /** Pass true to bypass CONFLICT_DETECTED on retry. */
  force?: boolean;
}

export type CreateScheduleEntryResult = ScheduleRpcResult<{
  success: true;
  entry_id: string;
  conflicts?: ConflictDetail[];
  already_created?: boolean;
}>;

export function useCreateScheduleEntry() {
  return useCallback(
    async (input: CreateScheduleEntryInput): Promise<CreateScheduleEntryResult> => {
      return callScheduleRpc<{
        success: true;
        entry_id: string;
        conflicts?: ConflictDetail[];
        already_created?: boolean;
      }>('create_schedule_entry', {
        p_type: input.type,
        p_title: input.title,
        p_start_at: input.startAt,
        p_end_at: input.endAt,
        p_assigned_to: input.assignedTo ?? null,
        p_resource_id: input.resourceId ?? null,
        p_job_id: input.jobId ?? null,
        p_booking_id: input.bookingId ?? null,
        p_project_id: input.projectId ?? null,
        p_client_id: input.clientId ?? null,
        p_location_name: input.locationName ?? null,
        p_location_address: input.locationAddress ?? null,
        p_location_lat: input.locationLat ?? null,
        p_location_lng: input.locationLng ?? null,
        p_recurrence_rule: input.recurrenceRule ?? null,
        p_recurrence_end: input.recurrenceEnd ?? null,
        p_color: input.color ?? null,
        p_capacity: input.capacity ?? null,
        p_notes: input.notes ?? null,
        p_idempotency_key: input.idempotencyKey,
        p_force: input.force ?? false,
      });
    },
    [],
  );
}
